import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import rfidService, { ReaderActivityLog } from '../../services/rfidService';
import toast from '../../utils/toast';
import { format } from 'date-fns';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/config/constants';

const ReaderActivityLogsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [activityLogs, setActivityLogs] = useState<ReaderActivityLog[]>([]);
  const [readerName, setReaderName] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    if (id) {
      fetchReaderDetails(id);
      fetchActivityLogs(id, page, rowsPerPage);
    }
  }, [id, page, rowsPerPage]);

  const fetchReaderDetails = async (readerId: string) => {
    try {
      const reader = await rfidService.getReaderById(readerId);
      setReaderName(reader.name);
    } catch (error) {
      console.error('Error fetching reader details:', error);
      toast.error('Failed to fetch reader details');
    }
  };

  const fetchActivityLogs = async (readerId: string, page: number, rowsPerPage: number) => {
    try {
      setLoading(true);
      const response = await rfidService.getReaderActivityLogs(readerId, {
        page: page + 1,
        page_size: rowsPerPage,
        sort_by: 'timestamp',
        sort_order: 'desc',
      });
      
      setActivityLogs(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActivityTypeLabel = (activityType: string) => {
    switch (activityType) {
      case 'CONNECT':
        return 'Connect';
      case 'DISCONNECT':
        return 'Disconnect';
      case 'AUTH_SUCCESS':
        return 'Authentication Success';
      case 'AUTH_FAILURE':
        return 'Authentication Failure';
      case 'DATA_RECEIVED':
        return 'Data Received';
      case 'ERROR':
        return 'Error';
      default:
        return activityType;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={`/rfid/readers/${id}`}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Activity Logs: {readerName}
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Activity Type</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLogs.length > 0 ? (
                      activityLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                          <TableCell>{getActivityTypeLabel(log.activity_type)}</TableCell>
                          <TableCell>
                            {log.details ? JSON.stringify(log.details) : 'No details'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No activity logs found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReaderActivityLogsPage;